<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div>
        <h1 class="text-lg font-semibold text-gray-900">Factures fournisseurs</h1>
        <p class="text-xs text-gray-400">{{ pagination.total || 0 }} facture(s) au total</p>
      </div>
      <button
        v-permission="'supplier-invoices.create'"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
        @click="onAddInvoice">
        <i class="fas fa-plus text-[10px]"></i>
        Nouvelle facture
      </button>
    </div>

    <!-- Statistiques -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
      <div class="bg-white rounded-xl border border-gray-200 flex items-center gap-3 p-4">
        <div class="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
          <i class="fas fa-clock text-orange-500 text-sm"></i>
        </div>
        <div>
          <p class="text-[11px] text-gray-400 uppercase tracking-wider">En attente</p>
          <p class="text-lg font-bold text-gray-900">{{ stats.pending }}</p>
        </div>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 flex items-center gap-3 p-4">
        <div class="w-9 h-9 rounded-lg bg-yellow-50 flex items-center justify-center flex-shrink-0">
          <i class="fas fa-circle-half-stroke text-yellow-500 text-sm"></i>
        </div>
        <div>
          <p class="text-[11px] text-gray-400 uppercase tracking-wider">Partielles</p>
          <p class="text-lg font-bold text-gray-900">{{ stats.partial }}</p>
        </div>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 flex items-center gap-3 p-4">
        <div class="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
          <i class="fas fa-check text-green-500 text-sm"></i>
        </div>
        <div>
          <p class="text-[11px] text-gray-400 uppercase tracking-wider">Payées</p>
          <p class="text-lg font-bold text-gray-900">{{ stats.paid }}</p>
        </div>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 flex items-center gap-3 p-4">
        <div class="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
          <i class="fas fa-file-lines text-blue-500 text-sm"></i>
        </div>
        <div>
          <p class="text-[11px] text-gray-400 uppercase tracking-wider">Total</p>
          <p class="text-lg font-bold text-gray-900">{{ pagination.total }}</p>
        </div>
      </div>
    </div>

    <!-- Filtres -->
    <div class="bg-white rounded-xl border border-gray-200 p-4 mb-5">
      <div class="flex flex-wrap gap-3 items-center">
        <div class="relative flex-1 min-w-[220px]">
          <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
          <input type="text" v-model="filters.search"
            placeholder="Rechercher par référence, fournisseur..."
            class="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
            @input="debouncedFetch" />
        </div>
        <select v-model="filters.status" @change="onStatusFilterChange"
          class="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors min-w-[180px]">
          <option value="">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="partial">Partiellement payée</option>
          <option value="paid">Payée</option>
          <option value="overdue">En retard</option>
          <option value="cancelled">Annulée</option>
        </select>
        <button
          class="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="fetchInvoices" title="Actualiser">
          <i class="fas fa-rotate text-xs"></i>
        </button>
      </div>
    </div>

    <!-- Tableau -->
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <!-- Chargement -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
        <p class="text-sm text-gray-400">Chargement des factures...</p>
      </div>

      <!-- État vide -->
      <div v-else-if="invoices.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
        <div class="text-gray-300 mb-3">
          <i class="fas fa-file-lines text-3xl"></i>
        </div>
        <p class="text-sm font-medium text-gray-500 mb-1">Aucune facture trouvée</p>
        <p class="text-xs text-gray-400 mb-4">
          <span v-if="filters.search || filters.status">Essayez de modifier vos critères de recherche.</span>
          <span v-else>Commencez par créer votre première facture fournisseur.</span>
        </p>
        <button v-if="!filters.search && !filters.status"
          v-permission="'supplier-invoices.create'"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          @click="onAddInvoice">
          <i class="fas fa-plus text-[10px]"></i>
          Créer la première facture
        </button>
      </div>

      <!-- Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full min-w-[800px]">
          <thead class="bg-gray-50/80">
            <tr>
              <th class="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Référence</th>
              <th class="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Fournisseur</th>
              <th class="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">Date</th>
              <th class="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-32">Montant</th>
              <th class="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-32">Statut</th>
              <th class="px-3 py-2.5 w-12"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50">
            <tr v-for="invoice in invoices" :key="invoice.id"
              class="hover:bg-gray-50/60 transition-colors cursor-pointer"
              @click="goToDetails(invoice.id)">
              <td class="px-5 py-3">
                <span class="text-sm font-medium text-gray-900">{{ invoice.invoice_number }}</span>
              </td>
              <td class="px-3 py-3">
                <span class="text-sm text-gray-900">{{ invoice.supplier?.name || '-' }}</span>
              </td>
              <td class="px-3 py-3">
                <span class="text-xs text-gray-500">
                  <i class="fas fa-calendar mr-1 text-[10px] text-gray-300"></i>
                  {{ formatDate(invoice.invoice_date) }}
                </span>
              </td>
              <td class="px-3 py-3 text-right">
                <p class="text-sm font-semibold text-gray-900">{{ formatCurrency(invoice.total_amount) }}</p>
                <p v-if="invoice.paid_amount > 0" class="text-[11px] text-green-600 font-medium mt-0.5">
                  Payé: {{ formatCurrency(invoice.paid_amount) }}
                </p>
              </td>
              <td class="px-3 py-3 text-center">
                <span :class="[
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold',
                  getStatusVariant(invoice.status) === 'warning' ? 'bg-orange-50 text-orange-600' :
                  getStatusVariant(invoice.status) === 'success' ? 'bg-green-50 text-green-600' :
                  getStatusVariant(invoice.status) === 'info' ? 'bg-blue-50 text-blue-600' :
                  getStatusVariant(invoice.status) === 'danger' ? 'bg-red-50 text-red-600' :
                  'bg-gray-100 text-gray-500'
                ]">
                  <span class="w-1.5 h-1.5 rounded-full bg-current"></span>
                  {{ getStatusLabel(invoice.status) }}
                </span>
              </td>
              <td class="px-3 py-3 text-center">
                <button
                  class="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                  @click.stop="goToDetails(invoice.id)" title="Voir les détails">
                  <i class="fas fa-eye text-[11px]"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <PaginationComponent
        v-if="invoices.length > 0"
        :current-page="pagination.page"
        :per-page="pagination.per_page"
        :total="pagination.total"
        :to="pagination.to"
        :from="pagination.from"
        @page-change="goToPage"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import supplierInvoiceService from '@/services/supplierInvoiceService'

export default defineComponent({
  name: 'SupplierInvoicesView',
  components: {
    PaginationComponent
  },
  setup() {
    const loading = ref(false)
    const router = useRouter()

    const invoices = ref([])
    const filters = ref({
      search: '',
      status: ''
    })
    const pagination = ref({
      page: 1,
      per_page: 10,
      total: 0,
      from: 0,
      to: 0
    })
    let debounceTimer = null

    const stats = computed(() => {
      if (!Array.isArray(invoices.value)) return { pending: 0, partial: 0, paid: 0 }
      const pending = invoices.value.filter(i => i.status === 'pending').length
      const partial = invoices.value.filter(i => i.status === 'partial').length
      const paid = invoices.value.filter(i => i.status === 'paid').length
      return { pending, partial, paid }
    })

    const fetchInvoices = async () => {
      try {
        loading.value = true
        const res = await supplierInvoiceService.getSupplierInvoices({
          search: filters.value.search || undefined,
          status: filters.value.status || undefined,
          per_page: pagination.value.per_page,
          page: pagination.value.page
        })
        const data = res.data
        if (data) {
          if (Array.isArray(data)) {
            invoices.value = data
          } else if (data.data && Array.isArray(data.data)) {
            invoices.value = data.data
            pagination.value.total = Number(data.total || 0)
            pagination.value.per_page = Number(data.per_page || pagination.value.per_page)
            pagination.value.page = Number(data.current_page || pagination.value.page)
            pagination.value.from = Number(data.from ?? ((pagination.value.page - 1) * pagination.value.per_page + 1))
            pagination.value.to = Number(data.to ?? Math.min(pagination.value.page * pagination.value.per_page, pagination.value.total))
          } else if (data.items && Array.isArray(data.items)) {
            invoices.value = data.items
          } else if (data.meta) {
            invoices.value = data.data || []
            pagination.value.total = Number(data.meta.total || 0)
            pagination.value.per_page = Number(data.meta.per_page || pagination.value.per_page)
            pagination.value.page = Number(data.meta.current_page || pagination.value.page)
            pagination.value.from = Number(data.meta.from ?? data.from ?? ((pagination.value.page - 1) * pagination.value.per_page + 1))
            pagination.value.to = Number(data.meta.to ?? data.to ?? Math.min(pagination.value.page * pagination.value.per_page, pagination.value.total))
          } else {
            invoices.value = []
          }
        } else {
          invoices.value = []
        }

        if (!pagination.value.total) {
          pagination.value.total = Array.isArray(invoices.value) ? invoices.value.length : 0
        }
        if (!pagination.value.per_page) {
          pagination.value.per_page = 10
        }
        if (!pagination.value.page) {
          pagination.value.page = 1
        }
        if (!pagination.value.from && pagination.value.total > 0) {
          pagination.value.from = (pagination.value.page - 1) * pagination.value.per_page + 1
        }
        if (!pagination.value.to && pagination.value.total > 0) {
          pagination.value.to = Math.min(pagination.value.page * pagination.value.per_page, pagination.value.total)
        }
      } catch (e) {
        console.error('Erreur chargement factures:', e)
        invoices.value = []
      } finally {
        loading.value = false
      }
    }

    const debouncedFetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        pagination.value.page = 1
        fetchInvoices()
      }, 300)
    }

    const onStatusFilterChange = () => {
      pagination.value.page = 1
      fetchInvoices()
    }

    const goToPage = (page) => {
      pagination.value.page = page
      fetchInvoices()
    }

    const onAddInvoice = () => {
      router.push({ name: 'suppliers' })
    }

    const goToDetails = (id) => {
      router.push({ name: 'supplier-invoice-details', params: { id } })
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      return supplierInvoiceService.formatDate(dateStr)
    }

    const formatCurrency = (amount) => {
      return supplierInvoiceService.formatCurrency(amount)
    }

    const getStatusLabel = (status) => {
      return supplierInvoiceService.getStatusLabel(status)
    }

    const getStatusVariant = (status) => {
      const map = {
        pending: 'warning',
        paid: 'success',
        partial: 'info',
        partial_paid: 'info',
        cancelled: 'danger',
        overdue: 'danger'
      }
      return map[status] || 'gray'
    }

    const getInitials = (name) => {
      if (!name) return '--'
      const parts = name.split(' ').filter(Boolean)
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }

    fetchInvoices()

    return {
      loading,
      invoices,
      filters,
      pagination,
      stats,
      debouncedFetch,
      onStatusFilterChange,
      goToPage,
      onAddInvoice,
      formatDate,
      formatCurrency,
      goToDetails,
      getStatusLabel,
      getStatusVariant,
      getInitials,
      fetchInvoices
    }
  }
})
</script>
